import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/*  Meta Graph API constants                                                   */
/*                                                                             */
/*  Verified against official Meta documentation on 2026-08-10.                */
/*  See docs/DOC_RESEARCH.md for the full source list.                         */
/* -------------------------------------------------------------------------- */
/**
 * Pinned Graph API version. v26.0 was released 2026-07-29 and is the newest
 * version listed at https://developers.facebook.com/docs/graph-api/changelog/versions/
 *
 * We pin explicitly rather than calling the unversioned endpoint so that a Meta
 * release can never silently change behaviour underneath us.
 */
export const GRAPH_API_VERSION = 'v26.0';
export const GRAPH_HOST = 'https://graph.facebook.com';
export const FACEBOOK_WWW_HOST = 'https://www.facebook.com';
/**
 * OAuth scopes requested at login.
 *
 * Each entry documents *why* it is requested — this list is mirrored in
 * docs/PERMISSIONS.md, which is what you paste into the App Review submission.
 */
export const OAUTH_SCOPES = [
    /** Enumerate the Pages the user manages, so we can show them in the dashboard. */
    'pages_show_list',
    /** Read the Page's own posts (source mode "owned") and Page metadata. */
    'pages_read_engagement',
    /** Create posts/photos/videos/stories on Pages the user administers. */
    'pages_manage_posts',
    /** Basic identity of the connecting user, for the Connections panel. */
    'public_profile',
];
/**
 * Optional feature, NOT a scope. Required to read posts of public Pages the
 * connecting user does not manage. Requires App Review + Business Verification.
 * Gated behind META_ENABLE_PUBLIC_PAGE_SOURCE.
 */
export const PAGE_PUBLIC_CONTENT_ACCESS_FEATURE = 'Page Public Content Access';
/**
 * Reading the comments other people left on your Page's posts requires
 * `pages_read_user_content`, which needs App Review. It is NOT in OAUTH_SCOPES
 * above: publishing your own comments does not need it, and requesting a
 * permission you do not exercise is a common review rejection.
 *
 * Requesting it is opt-in, gated by META_ENABLE_USER_COMMENT_REPLIES. When that
 * flag is on, this scope is appended to the login request and the reply-to-user
 * endpoints become available.
 */
export const USER_CONTENT_SCOPE = 'pages_read_user_content';
/**
 * Required to subscribe a Page to webhooks via POST /{page-id}/subscribed_apps.
 *
 * Also not in the base set: without comment automation we never subscribe to
 * anything, so requesting it would be asking for a capability we do not use.
 * Added when META_ENABLE_COMMENT_AUTOMATION is on.
 */
export const PAGE_METADATA_SCOPE = 'pages_manage_metadata';
/**
 * Required to publish comments as the Page.
 *
 * Not in the base set, for a reason learned the hard way: Meta rejects the
 * **entire** authorization dialog if any single requested permission is not
 * available to the app, and `pages_manage_engagement` must be added explicitly
 * in the App Dashboard. Leaving it in the base set meant a missing comment
 * permission blocked publishing too — a secondary feature taking down the
 * primary one.
 *
 * Now it is opt-in: with it off, publishing works and the comment endpoints
 * refuse with an explanation instead.
 */
export const PAGE_COMMENTS_SCOPE = 'pages_manage_engagement';
/**
 * Reach and impressions — how many people a post reached, not how many replied.
 *
 * Its own App Review item, so it is opt-in like the others: with the flag off
 * the collector simply leaves those columns null and the analytics screen shows
 * what it does have. Turning it on changes nothing until someone reconnects,
 * because a scope is granted at the consent dialog and not at deploy time.
 */
export const INSIGHTS_SCOPE = 'read_insights';
/**
 * The Instagram account attached to a Page.
 *
 * Without this, Meta does not say a Page has no Instagram account — it drops
 * the field from the response entirely and returns the rest, which is
 * indistinguishable from "there is none". Confirmed on 2026-08-24: asking for
 * `instagram_business_account{id,username}` came back as the Page id alone.
 *
 * Off by default. It is its own App Review item, and requesting a permission
 * that has not been approved puts it in front of every person who connects for
 * something the app cannot yet do.
 */
export const INSTAGRAM_SCOPE = 'instagram_basic';
/**
 * Private conversations, on Messenger and on Instagram.
 *
 * Two permissions rather than one: Meta answers `/{page}/conversations` with
 * `(#200) Requires permission: pages_messaging` for Messenger and `(#230)
 * Requires instagram_manage_messages` for Instagram, so each is refused on its
 * own terms. Confirmed against a live Page on 2026-08-24, where the Page role
 * already carried the MESSAGING task — the gap was the app permission, not the
 * person's authority.
 *
 * Both off by default. Each is its own App Review item, and both fall under
 * stricter handling than the Page permissions: a private message is not a
 * public comment.
 */
/**
 * Publishing to the Instagram account linked to a Page.
 *
 * Separate from `instagram_basic`, which only reveals that the account exists.
 * Held to its own flag for the same reason as the messaging ones: it is its own
 * App Review item, and an installation that only reads Instagram comments
 * should not be asking every person who connects for the right to post as them.
 *
 * Implies `instagram_basic` — there is nothing to publish to until the linked
 * account is visible.
 */
export const INSTAGRAM_PUBLISH_SCOPE = 'instagram_content_publish';
export const MESSAGING_SCOPE = 'pages_messaging';
export const IG_MESSAGING_SCOPE = 'instagram_manage_messages';
/**
 * Answering a Messenger thread after the 24-hour window has closed.
 *
 * Inside the window a Page may send free text. Outside it Meta accepts only a
 * pre-approved UTILITY template — order and account updates, appointment
 * reminders — and refuses anything promotional. The message tags that used to
 * cover these cases (CONFIRMED_EVENT_UPDATE, ACCOUNT_UPDATE,
 * POST_PURCHASE_UPDATE) return error 100 as of 27 April 2026, so this is now the
 * only route.
 *
 * Verified against Meta's Messenger Platform docs on 2026-08-23:
 * https://developers.facebook.com/documentation/business-messaging/messenger-platform/send-messages/utility-messages
 */
export const UTILITY_MESSAGING_SCOPE = 'pages_utility_messaging';
/**
 * Page tasks returned by GET /me/accounts?fields=tasks.
 * We require CREATE_CONTENT before we will allow a Page to be a publish target.
 */
export const PAGE_TASKS = [
    'ADVERTISE',
    'ANALYZE',
    'CREATE_CONTENT',
    'MESSAGING',
    'MODERATE',
    'MANAGE',
];
/** Task required to publish posts/photos/videos/stories on a Page. */
export const TASK_REQUIRED_TO_PUBLISH = 'CREATE_CONTENT';
/** Task required to publish comments on a Page's posts. */
export const TASK_REQUIRED_TO_COMMENT = 'MODERATE';
/* -------------------------------------------------------------------------- */
/*  Domain enums                                                               */
/* -------------------------------------------------------------------------- */
export const SourceKind = {
    /** A post read from a Page the user manages. */
    OWNED_PAGE_POST: 'OWNED_PAGE_POST',
    /** A post read from a public Page via Page Public Content Access. */
    PUBLIC_PAGE_POST: 'PUBLIC_PAGE_POST',
    /** Content composed by the operator inside this system. */
    COMPOSITION: 'COMPOSITION',
    /** Comments written by the operator, targeting an existing post or comment. */
    COMMENT_BATCH: 'COMMENT_BATCH',
};
export const PublishKind = {
    TEXT: 'TEXT',
    LINK: 'LINK',
    PHOTO: 'PHOTO',
    MULTI_PHOTO: 'MULTI_PHOTO',
    VIDEO: 'VIDEO',
    PHOTO_STORY: 'PHOTO_STORY',
    VIDEO_STORY: 'VIDEO_STORY',
    /** A Facebook Reel: one vertical video, through the Pages Reels API. */
    REEL: 'REEL',
    COMMENT: 'COMMENT',
};
/**
 * Extra shapes one post can also go out as, on the same Page.
 *
 * Not a publish kind of their own: the post keeps its kind, and each of these
 * becomes an additional target that settles on its own.
 */
export const AlsoAs = {
    STORY: 'STORY',
    REEL: 'REEL',
};
/**
 * Which "also publish as" choices a composition can take.
 *
 * One definition for the composer's checkboxes and the server's check, so the
 * screen never offers what the API will refuse. A Story needs exactly one photo
 * or video; a Reel needs exactly one video. A post that already is a Story or a
 * Reel does not offer itself again.
 */
export function alsoAsAvailability(kind, media) {
    const single = media.length === 1;
    return {
        story: single && kind !== PublishKind.PHOTO_STORY && kind !== PublishKind.VIDEO_STORY,
        reel: single && media[0] === 'video' && kind !== PublishKind.REEL,
    };
}
/** The reason a requested "also as" cannot be honoured, or null when it can. */
export function alsoAsProblem(kind, media, alsoAs, scheduled) {
    if (alsoAs.length === 0)
        return null;
    const available = alsoAsAvailability(kind, media);
    if (alsoAs.includes(AlsoAs.STORY)) {
        if (!available.story) {
            return 'A Story needs exactly one photo or video, and the post must not already be a Story.';
        }
        if (scheduled)
            return 'The Page Stories API does not support scheduled publishing';
    }
    if (alsoAs.includes(AlsoAs.REEL) && !available.reel) {
        return 'A Reel needs exactly one video, and the post must not already be a Reel.';
    }
    return null;
}
/**
 * The extra targets are separate destinations, prefixed like Instagram's, so the
 * unique index that stops a double publish gives each its own slot: the post,
 * its Story and its Reel may all go to one Page, and none of them twice.
 */
export function buildVariantDestinationKey(variant, pageId) {
    return `${variant === AlsoAs.STORY ? 'story' : 'reel'}:${pageId}`;
}
/** Which extra shape a target's destination is, or null for the post itself. */
export function variantOfDestinationKey(destinationKey) {
    if (destinationKey.startsWith('story:'))
        return AlsoAs.STORY;
    if (destinationKey.startsWith('reel:'))
        return AlsoAs.REEL;
    return null;
}
export const JobStatus = {
    /**
     * Created, held, and not yet queued for a worker.
     *
     * Distinct from QUEUED on purpose: a QUEUED job is one the worker has simply
     * not reached yet, and the operator can do nothing but wait. A SCHEDULED one
     * is waiting on a clock the operator set and can still change or cancel, and
     * a screen that shows them the same way makes the second look stuck.
     */
    SCHEDULED: 'SCHEDULED',
    QUEUED: 'QUEUED',
    RUNNING: 'RUNNING',
    SUCCEEDED: 'SUCCEEDED',
    /** Every target failed. */
    FAILED: 'FAILED',
    /** Some targets succeeded, some failed (requirement 13). */
    PARTIAL: 'PARTIAL',
    CANCELLED: 'CANCELLED',
};
export const TargetStatus = {
    PENDING: 'PENDING',
    /** A Graph write is in flight; used to make retries safe. */
    IN_FLIGHT: 'IN_FLIGHT',
    SUCCEEDED: 'SUCCEEDED',
    FAILED: 'FAILED',
    /** Blocked by the dedup ledger — already published to this Page. */
    SKIPPED_DUPLICATE: 'SKIPPED_DUPLICATE',
    /**
     * The Graph call may or may not have landed (crash/timeout mid-write).
     * Requires operator resolution; never auto-retried. See docs/LIMITATIONS.md.
     */
    NEEDS_RECONCILIATION: 'NEEDS_RECONCILIATION',
};
/* -------------------------------------------------------------------------- */
/*  API request schemas                                                        */
/* -------------------------------------------------------------------------- */
export const loginSchema = z.object({
    /*
     * Normalised the same way every other email field in this system is.
     *
     * The local part of an address is case-sensitive by the letter of the RFC and
     * by nobody's expectation in practice. Every path that *writes* a user —
     * signup, admin creation, password reset — lower-cases first, so an address
     * stored here is always lower-case. This schema was the one that did not,
     * which meant an account created as sam@example.com simply could not be
     * signed into as Sam@Example.com: the lookup found nothing and the answer was
     * "invalid email or password".
     */
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(6).max(200),
});
export const listSourcePostsQuerySchema = z.object({
    pageId: z.string().min(1),
    limit: z.coerce.number().int().min(1).max(50).default(25),
    after: z.string().optional(),
});
export const resolvePublicPageSchema = z.object({
    /** Accepts a full facebook.com URL, a @handle, a username, or a numeric ID. */
    reference: z.string().min(1).max(500),
});
const mediaAssetIdSchema = z.string().uuid();
export const compositionSchema = z
    .object({
    kind: z.enum([
        PublishKind.TEXT,
        PublishKind.LINK,
        PublishKind.PHOTO,
        PublishKind.MULTI_PHOTO,
        PublishKind.VIDEO,
        PublishKind.PHOTO_STORY,
        PublishKind.VIDEO_STORY,
        PublishKind.REEL,
    ]),
    message: z.string().max(63206).optional(),
    link: z.string().url().optional(),
    mediaAssetIds: z.array(mediaAssetIdSchema).max(10).default([]),
    /** Optional first comment posted under the new post on each target Page. */
    firstComment: z.string().max(8000).optional(),
    /** Unix seconds. Meta accepts 10 minutes .. 75 days ahead. */
    scheduledPublishTime: z.number().int().positive().optional(),
})
    .superRefine((value, ctx) => {
    const requireMedia = (min, max) => {
        if (value.mediaAssetIds.length < min || value.mediaAssetIds.length > max) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['mediaAssetIds'],
                message: `${value.kind} requires between ${min} and ${max} media asset(s)`,
            });
        }
    };
    switch (value.kind) {
        case PublishKind.TEXT:
            if (!value.message?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['message'],
                    message: 'A text post requires a message',
                });
            }
            break;
        case PublishKind.LINK:
            if (!value.link) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['link'],
                    message: 'A link post requires a link',
                });
            }
            break;
        case PublishKind.PHOTO:
        case PublishKind.PHOTO_STORY:
            requireMedia(1, 1);
            break;
        case PublishKind.MULTI_PHOTO:
            requireMedia(2, 10);
            break;
        case PublishKind.VIDEO:
        case PublishKind.VIDEO_STORY:
        case PublishKind.REEL:
            requireMedia(1, 1);
            break;
    }
    // Stories do not carry a caption or a scheduled time in the Stories API.
    if ((value.kind === PublishKind.PHOTO_STORY || value.kind === PublishKind.VIDEO_STORY) &&
        value.scheduledPublishTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scheduledPublishTime'],
            message: 'The Page Stories API does not support scheduled publishing',
        });
    }
});
/* -------------------------------------------------------------------------- */
/*  Comments                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * What a comment can be attached to.
 *
 * POST — a post on a Page you manage. Works with the base scopes.
 * COMMENT — a reply to an existing comment. Reading the comment you are
 *           replying to needs `pages_read_user_content`, so this is gated.
 */
export const CommentTargetKind = {
    POST: 'POST',
    COMMENT: 'COMMENT',
};
export const commentDraftSchema = z.object({
    message: z.string().trim().min(1).max(8000),
    /** Optional image, uploaded through the same media pipeline as posts. */
    mediaAssetId: z.string().uuid().optional(),
});
/**
 * Meta's own guidance is that rapid repeated writes look like automation. We cap
 * a single batch and space the writes out server-side; this is the hard ceiling.
 */
export const MAX_COMMENTS_PER_BATCH = 10;
export const createCommentJobSchema = z.object({
    targetKind: z
        .enum([CommentTargetKind.POST, CommentTargetKind.COMMENT])
        .default(CommentTargetKind.POST),
    /** Local SourcePost row id — always a post on a Page the operator manages. */
    sourcePostId: z.string().uuid(),
    /**
     * Set only when targetKind is COMMENT: the Graph id of the comment being
     * replied to. It must belong to the post identified by sourcePostId.
     */
    parentCommentId: z.string().min(1).max(200).optional(),
    /** Published in order, spaced apart in time. */
    comments: z.array(commentDraftSchema).min(1).max(MAX_COMMENTS_PER_BATCH),
});
export const listCommentsQuerySchema = z.object({
    sourcePostId: z.string().uuid(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * How far ahead a job may be scheduled.
 *
 * Ninety days. Not a technical limit — BullMQ would hold a delay of any size —
 * but a delay long enough to outlive the token that will execute it is a job
 * that fails silently in three months, and a Page token's life is measured in
 * weeks. Better to refuse it now than to accept it and disappoint later.
 */
export const MAX_SCHEDULE_AHEAD_MS = 90 * 24 * 60 * 60 * 1000;
export const createJobSchema = z
    .object({
    sourceKind: z.enum([
        SourceKind.OWNED_PAGE_POST,
        SourceKind.PUBLIC_PAGE_POST,
        SourceKind.COMPOSITION,
    ]),
    /** Required when sourceKind is a post: the local SourcePost row id. */
    sourcePostId: z.string().uuid().optional(),
    /** Required when sourceKind is COMPOSITION: the local Composition row id. */
    compositionId: z.string().uuid().optional(),
    /** Local Page row ids the operator selected as publish targets. */
    targetPageIds: z.array(z.string().uuid()).max(50).default([]),
    /** Local SocialAccount row ids (LinkedIn and the other non-Meta channels). */
    socialAccountIds: z.array(z.string().uuid()).max(50).default([]),
    /** Pages whose linked Instagram account is a destination in its own right. */
    instagramPageIds: z.array(z.string().uuid()).max(50).default([]),
    /** Operator override to publish again to a Page that already received it. */
    allowRepublish: z.boolean().default(false),
    /**
     * Also publish to the Instagram account linked to each target Page.
     *
     * A separate destination rather than a separate job: the operator's intent
     * is "put this out", and making them build the same post twice is the kind
     * of duplication the whole product exists to remove. Pages with no linked
     * account are reported as skipped rather than silently dropped.
     */
    alsoInstagram: z.boolean().default(false),
    /**
     * Also publish the same post as a Story and/or a Reel on each target Page.
     *
     * Each is its own target with its own duplicate slot, so a retry never
     * repeats one and a failed Story does not fail the post. Checked against the
     * composition's media when the job is created (a Story needs exactly one
     * photo or video, a Reel exactly one video).
     */
    alsoAs: z
        .array(z.enum([AlsoAs.STORY, AlsoAs.REEL]))
        .max(2)
        .default([])
        .refine((list) => new Set(list).size === list.length, 'alsoAs must not repeat a choice'),
    /**
     * Publish at this moment instead of now.
     *
     * ISO 8601, and the client sends an absolute instant rather than a local
     * time plus a zone: "nine on Thursday" means nine where the operator is,
     * and resolving that in the browser — which knows the zone — beats storing
     * a zone name the server has to interpret months later.
     *
     * Absent means now, which is what every job did before this existed.
     */
    scheduledAt: z.coerce.date().optional(),
})
    .superRefine((value, ctx) => {
    if (value.targetPageIds.length + value.socialAccountIds.length + value.instagramPageIds.length ===
        0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetPageIds'],
            message: 'Select at least one target',
        });
    }
    const needsPost = value.sourceKind === SourceKind.OWNED_PAGE_POST ||
        value.sourceKind === SourceKind.PUBLIC_PAGE_POST;
    if (needsPost && !value.sourcePostId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sourcePostId'],
            message: 'sourcePostId is required for post-based jobs',
        });
    }
    /*
     * A schedule in the past is almost always a timezone mistake rather than an
     * instruction to publish immediately, and publishing immediately is the one
     * outcome that cannot be undone. A minute of slack absorbs the round trip.
     */
    if (value.scheduledAt && value.scheduledAt.getTime() < Date.now() - 60_000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduledAt is in the past',
        });
    }
    if (value.scheduledAt && value.scheduledAt.getTime() > Date.now() + MAX_SCHEDULE_AHEAD_MS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduledAt is more than 90 days away',
        });
    }
    if (value.sourceKind === SourceKind.COMPOSITION && !value.compositionId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['compositionId'],
            message: 'compositionId is required for composition jobs',
        });
    }
});
/* -------------------------------------------------------------------------- */
/*  What a person may do                                                       */
/* -------------------------------------------------------------------------- */
/** Everything a caller can be permitted to do. *
 * Lives here rather than in the API because the dashboard needs it too, and
 * the one time it did not, the two sides disagreed in silence: the server sent
 * `pages.read` and the browser asked whether it held `PAGES_READ`. Nothing
 * matched, every tab with a requirement vanished, and an administrator was
 * left with two of sixteen screens. A string on one side and a constant on the
 * other is a mismatch nothing can catch; one definition both sides import is a
 * compile error.
 */
export const Capability = {
    /** Read the unified inbox: comments, messages, saved replies, mentions. */
    INBOX_READ: 'inbox.read',
    /** Answer, dismiss, or send a utility template. */
    INBOX_WRITE: 'inbox.write',
    /**
     * Read one post because the inbox pointed at it.
     *
     * Deliberately separate from browsing the posts list. Somebody answering a
     * comment needs to see what the comment is *about* — the post it sits under,
     * opened from the item itself. That is not the same permission as reviewing
     * everything the account has published, and giving one to get the other is
     * how a role stops meaning anything.
     */
    POSTS_CONTEXT: 'posts.context',
    /** Browse published, scheduled, cancelled and failed posts. */
    POSTS_READ: 'posts.read',
    /** Compose, schedule, edit, retry, cancel, upload media. */
    POSTS_WRITE: 'posts.write',
    /** Reach, comments and reactions over a range. */
    INSIGHTS_READ: 'insights.read',
    /**
     * See which Meta accounts are connected and what they granted.
     *
     * Split out of `settings.read` for the reviewer role. Meta's reviewer has to
     * be shown the connected account and its permissions — that is step two of
     * every submission — and `settings.read` would have handed them the
     * automation, the agent wiring and the call log along with it.
     */
    CONNECTIONS_READ: 'connections.read',
    /**
     * See this tenant's own account: its plan, its limits and its usage.
     *
     * Everybody who belongs to the business has it. Meta's reviewer does not:
     * they are a guest sent to inspect two Pages, and what the business pays is
     * neither their concern nor something to put in front of them.
     */
    ACCOUNT_READ: 'account.read',
    /** See the Pages on the account and their profiles. */
    PAGES_READ: 'pages.read',
    /** Sync Pages, change a profile picture, assign a WhatsApp number. */
    PAGES_WRITE: 'pages.write',
    /**
     * See configuration: connections, agents, keys, colleagues, logs.
     *
     * A viewer is explicitly outside this. "Read-only" was asked for over the
     * work — Pages, posts, comments, inbox, performance — and not over the
     * account's credentials and colleagues, which is a different kind of looking.
     */
    SETTINGS_READ: 'settings.read',
    /** Change any of it. */
    SETTINGS_WRITE: 'settings.write',
    /** Cross-tenant operation of the installation itself. */
    PLATFORM_ADMIN: 'platform.admin',
};
/**
 * What counts as one conversation.
 *
 * The inbox was a flat list, so a customer who wrote on Tuesday and again on
 * Thursday arrived as two strangers and whoever answered had no idea the first
 * conversation had happened. This is the key that joins them.
 *
 * It differs by channel, and both answers are decisions rather than mechanics:
 *
 *   - **A message thread** is the thread Meta already keeps. Nothing to decide;
 *     Messenger and WhatsApp both hand us a thread id, and where they do not,
 *     the person we are talking to is the thread.
 *   - **A comment thread is one person under one post.** Two people arguing
 *     under the same post are two conversations, not one — answering them in a
 *     single stream would put a stranger's words in the middle of somebody's
 *     reply. The same person commenting three times is one conversation.
 *
 * Where Meta withholds the author — it does for some comment sources — the
 * comment stands alone under its own id. That loses the thread, which is bad;
 * merging every anonymous commenter on a post into one conversation would be
 * worse, and this is the same reasoning `externalUserId` already applies.
 *
 * The page row id is part of every key, so a key is unique across the install
 * and two tenants holding the same Page never share a conversation.
 *
 * Kept in sync with the backfill in `20260910000000_conversations`, which
 * computes the same two shapes in SQL. Changing one means changing both.
 */
export function conversationKeyFor(item) {
    if (item.channel === 'PAGE_COMMENT') {
        const who = item.authorId ?? item.externalCommentId;
        return `c:${item.pageId}:${item.externalPostId ?? ''}:${who}`;
    }
    const thread = item.threadId ?? item.recipientId ?? item.externalCommentId;
    return `m:${item.pageId}:${thread}`;
}
/**
 * Change a post that has not gone out yet.
 *
 * Every field is optional and an absent one means "leave it" — the form sends
 * what the operator touched, and a scheduled post edited to fix a typo should
 * not have its hour rewritten by the same request.
 *
 * Targets are deliberately not here. Which pages a job goes to is bound up with
 * the duplicate ledger — each target holds a reservation on this content for
 * its page — so moving them means releasing and re-acquiring reservations, and
 * getting that half-right publishes twice. Cancelling and scheduling again is
 * the honest way to change pages, and it is one more click.
 */
export const editJobSchema = z
    .object({
    message: z.string().max(63_206).optional(),
    link: z.string().url().max(2048).nullable().optional(),
    firstComment: z.string().max(8000).nullable().optional(),
    /**
     * The files this post carries, as the whole list.
     *
     * Replaced rather than added to, because the form shows what is attached
     * and sends back what is left: a file removed on screen has to be a file
     * removed here, and an "add" verb cannot express that.
     */
    mediaAssetIds: z.array(z.string().uuid()).max(10).optional(),
    scheduledAt: z.coerce.date().optional(),
})
    .superRefine((value, ctx) => {
    // The same two rules the original schedule was held to. A post moved into
    // the past would publish the moment it was saved, which is the one outcome
    // that cannot be undone.
    if (value.scheduledAt && value.scheduledAt.getTime() < Date.now() - 60_000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduledAt is in the past',
        });
    }
    if (value.scheduledAt && value.scheduledAt.getTime() > Date.now() + MAX_SCHEDULE_AHEAD_MS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scheduledAt'],
            message: 'scheduledAt is more than 90 days away',
        });
    }
});
/**
 * The four things an operator actually asks the list.
 *
 * Statuses are the machine's vocabulary — seven of them, and `PARTIAL` means
 * nothing to the person who scheduled a post. These are the questions: did it
 * go out, is it still coming, did I stop it, did it break.
 *
 * Defined here rather than in the dashboard so that the grouping is decided
 * once. A tab that means one set of statuses in the browser and another on the
 * server produces counts that do not add up, and nobody can tell which half
 * is lying.
 */
export const JobView = {
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled',
    /**
     * With a worker, right now.
     *
     * Its own tab rather than a corner of the schedule. The schedule is what the
     * operator still controls — an hour they set, that they can move or call back
     * — and a post already being handed to Meta is none of those things. Mixed in,
     * it read as "scheduled" while it was in fact gone, and a row stuck here
     * (queued, never picked up) hid among posts that were merely waiting.
     *
     * Nearly always empty, which is the point: anything sitting in it for more
     * than a minute is a job that stalled, and that is worth seeing.
     */
    PUBLISHING: 'publishing',
    CANCELLED: 'cancelled',
    FAILED: 'failed',
};
/**
 * Which statuses each view holds. The four sets are disjoint and cover all
 * seven, so the tab counts add up to the total and no job is invisible.
 *
 * `PARTIAL` sits under *failed* rather than published, though it did publish
 * somewhere. That tab answers "what still needs me?", and a job that reached
 * three pages out of five needs somebody — its retry button is the reason the
 * tab exists. The row lists every target with its own status, so what did go
 * out is never in doubt.
 */
/**
 * The statuses whose content a person may still change.
 *
 * SCHEDULED is the obvious one — nothing has gone out. FAILED and PARTIAL are
 * here because of what the "needs attention" tab is for: the usual reason a
 * post failed is the post itself — a link Meta refused, a picture it would not
 * take, a word that tripped a filter — and retrying the same content
 * unchanged fails the same way. Offering only "retry" on a row that cannot
 * succeed is a button that lies.
 *
 * QUEUED and RUNNING are deliberately absent: a worker may publish between the
 * read and the write. SUCCEEDED and CANCELLED are settled records.
 *
 * Here rather than in either half, so the button the browser shows and the
 * request the server accepts cannot drift apart.
 */
export const JOB_EDITABLE_STATUSES = [
    JobStatus.SCHEDULED,
    JobStatus.FAILED,
    JobStatus.PARTIAL,
];
/**
 * And of those, the one that still has an hour to move.
 *
 * A failed post has already had its moment; what it needs is a correction and
 * another try, not a new appointment. Sending it one would write a time into a
 * row that the retry ignores — the queue takes a failed target immediately.
 */
export const JOB_RESCHEDULABLE_STATUSES = [JobStatus.SCHEDULED];
export const JOB_VIEW_STATUSES = {
    [JobView.PUBLISHED]: [JobStatus.SUCCEEDED],
    /*
     * Only what is still waiting on a clock.
     *
     * QUEUED and RUNNING used to be here, on the argument that to the person who
     * scheduled a post it has not gone out either way. In use that was wrong:
     * this tab is the one somebody opens to see what they can still change, and
     * every row in it that could not be changed was noise in the one list that
     * had to be trustworthy.
     */
    [JobView.SCHEDULED]: [JobStatus.SCHEDULED],
    [JobView.PUBLISHING]: [JobStatus.QUEUED, JobStatus.RUNNING],
    [JobView.CANCELLED]: [JobStatus.CANCELLED],
    [JobView.FAILED]: [JobStatus.FAILED, JobStatus.PARTIAL],
};
export const listJobsQuerySchema = z.object({
    status: z
        .enum([
        JobStatus.SCHEDULED,
        JobStatus.QUEUED,
        JobStatus.RUNNING,
        JobStatus.SUCCEEDED,
        JobStatus.FAILED,
        JobStatus.PARTIAL,
        JobStatus.CANCELLED,
    ])
        .optional(),
    /** One of the tabs. Ignored when `status` names a single status. */
    view: z
        .enum([
        JobView.PUBLISHED,
        JobView.SCHEDULED,
        JobView.PUBLISHING,
        JobView.CANCELLED,
        JobView.FAILED,
    ])
        .optional(),
    /**
     * A window of time, on the date that means something in this tab.
     *
     * Which date that is follows the tab: the schedule is read by the hour a
     * post goes out, every other tab by when it was made. Both ends are
     * optional, so "since Sunday" and "before June" are askable on their own.
     *
     * `to` is a day, and a day means all of it: the server pushes it to the end
     * of that date rather than to midnight at its start, or "1 – 3 September"
     * would silently drop everything published on the third.
     */
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    /**
     * Which way round to read it.
     *
     * Offered rather than decided here. Each tab has a default that is right
     * most of the time — soonest first for the schedule, newest first for the
     * records — and the operator who wants the other one should not have to ask
     * anybody to change it.
     */
    sort: z.enum(['asc', 'desc']).optional(),
    /** Narrow to one page. Refused for a page the caller was not assigned. */
    channel: z.enum(['FACEBOOK', 'INSTAGRAM', 'LINKEDIN']).optional(),
    socialAccountId: z.string().uuid().optional(),
    pageId: z.string().optional(),
    /**
     * Show what was hidden. An administrator's question, and the server checks
     * that it is one — a hidden row is still the account's record.
     */
    includeHidden: z
        .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
        .transform((value) => value === true || value === 'true' || value === '1')
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).default(0),
});
export const PAGE_CHANNEL_KEYS = [
    'FACEBOOK',
    'INSTAGRAM',
    'WHATSAPP',
    'MESSENGER',
    'INSTAGRAM_DM',
    'WEBHOOKS',
];
/**
 * Why a service is not working, when it is not.
 *
 * The distinction between these is the whole point, and it is one this product
 * got wrong once already: a screen that says "not granted" about a permission
 * the app cannot even request sends somebody to reconnect over and over for
 * nothing. So:
 *
 *  - `ACTIVE`      — working now.
 *  - `NOT_LINKED`  — nothing to connect to. No Instagram account on this Page,
 *                    no WhatsApp number assigned to it. Fixed at Meta, or by
 *                    assigning one here.
 *  - `NOT_GRANTED` — the permission was asked for and not given. Reconnecting
 *                    and accepting fixes it — unless Meta has not approved it for
 *                    this app, in which case it withholds it without a decline
 *                    (`declinedScopes` stays empty) and only App Review fixes it.
 *                    The two cannot be told apart from the stored grant, so the
 *                    wording covers both — unless the install has said the
 *                    permission is under review, which is `PENDING_APPROVAL`.
 *  - `PENDING_APPROVAL` — the permission is with Meta for review and not granted
 *                    to this account. Reconnecting cannot help; the operator
 *                    waits, and an account that holds it (a tester) reads ACTIVE.
 *  - `OFF`         — this install does not ask for it at all. Nothing the
 *                    operator does will change it; an administrator turns it on.
 */
export const PAGE_CHANNEL_STATES = [
    'ACTIVE',
    'NOT_LINKED',
    'NOT_GRANTED',
    'PENDING_APPROVAL',
    'OFF',
];
/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */
export function canPublishToPage(tasks) {
    return tasks.includes(TASK_REQUIRED_TO_PUBLISH);
}
export function canCommentOnPage(tasks) {
    return tasks.includes(TASK_REQUIRED_TO_COMMENT);
}
/**
 * Stable identity of "the thing being published". Two jobs producing the same
 * key must never both land on the same destination — that is requirement 12.
 */
export function buildSourceKey(kind, identifier) {
    return `${kind}:${identifier}`;
}
/**
 * Identity of one comment within a batch. Each comment dedups independently, so
 * three different comments may go on one post, while re-running the job cannot
 * post any of them a second time.
 */
export function buildCommentSourceKey(baseKey, sequence) {
    return `${baseKey}#${sequence}`;
}
/**
 * Where a write lands.
 *
 * A post's destination is the Page. A comment's destination is the specific
 * object being commented on, because several comments may target different
 * objects on the same Page — the Page alone would not distinguish them.
 */
export function buildDestinationKey(pageId, targetObjectId) {
    return targetObjectId ? `${pageId}:${targetObjectId}` : pageId;
}
/* -------------------------------------------------------------------------- */
/*  The agent API                                                              */
/* -------------------------------------------------------------------------- */
/**
 * What an API key may be granted, per Page.
 *
 * Five rather than one "write" permission, because they differ in what a
 * mistake costs. A wrong post is embarrassing and deletable. A wrong reply
 * reaches one customer and cannot be recalled. Reading an inbox exposes private
 * conversations without changing anything. Collapsing those into a single
 * switch means every agent that needs the smallest of them gets all of them.
 */
export const ApiCapability = {
    /** List the Pages this key may touch, and what it may do on each. */
    PAGES_READ: 'PAGES_READ',
    /** Create a publish job — immediately, or for a later moment. */
    PUBLISH: 'PUBLISH',
    /** Read comments and messages waiting for an answer. Private content. */
    INBOX_READ: 'INBOX_READ',
    /** Answer one. Whether that sends or drafts is per grant, not per key. */
    INBOX_REPLY: 'INBOX_REPLY',
    /** Reach, impressions, per-post performance. Changes nothing. */
    INSIGHTS_READ: 'INSIGHTS_READ',
    /**
     * List a Page's own published posts, with the Meta post id on each. Changes
     * nothing — it exists because `comment` and `edit_post` both take a post id
     * and nothing granted only PUBLISH could previously discover one without
     * being told it out of band.
     */
    POSTS_READ: 'POSTS_READ',
    /**
     * Drive the content studio: generate a script, audio, video or image, and
     * review the result. Not Page-scoped — a generation job belongs to the
     * tenant, not to any one Page — so `pageId` is never checked for it and a
     * grant is written with `pageScope: '*'` by convention.
     */
    GENERATION_WRITE: 'GENERATION_WRITE',
};
export const API_CAPABILITIES = Object.values(ApiCapability);
/** `pageScope` meaning "every Page this tenant has, including future ones". */
export const ALL_PAGES = '*';
/** One cell of the matrix. */
export const apiKeyGrantSchema = z.object({
    /** A Page row id, or `*`. */
    pageScope: z.string().min(1).max(64),
    capability: z.enum([
        ApiCapability.PAGES_READ,
        ApiCapability.PUBLISH,
        ApiCapability.INBOX_READ,
        ApiCapability.INBOX_REPLY,
        ApiCapability.INSIGHTS_READ,
        ApiCapability.POSTS_READ,
        ApiCapability.GENERATION_WRITE,
    ]),
    /**
     * Only meaningful with INBOX_REPLY. Left false by default deliberately: the
     * setting that puts unreviewed words in front of a customer should be the one
     * somebody had to switch on.
     */
    autoSend: z.boolean().default(false),
});
export const issueApiKeySchema = z.object({
    name: z.string().trim().min(1).max(100),
    /**
     * At least one cell. A key with an empty matrix can do nothing, and issuing
     * one is always a mistake rather than a deliberate "no access" — the way to
     * express no access is not to issue a key.
     */
    grants: z.array(apiKeyGrantSchema).min(1).max(200),
    expiresInDays: z.number().int().min(1).max(3650).optional(),
});
/** Which surface a target writes to. */
export const PublishChannel = {
    FACEBOOK: 'FACEBOOK',
    /** The Instagram account linked to the Page. */
    INSTAGRAM: 'INSTAGRAM',
    /** A LinkedIn profile or organization page. */
    LINKEDIN: 'LINKEDIN',
};
/**
 * A network published to through a SocialAccount rather than a Meta Page: the
 * key of a network definition (a built-in preset or a row on the networks
 * screen). Any string, so a network added as data needs no change here. The
 * constant names the ones that ship built in.
 */
export const SocialProvider = {
    LINKEDIN: 'LINKEDIN',
};
/** A social target's channel is its network's key. */
export function channelForProvider(provider) {
    return provider;
}
/** True for a channel that publishes through a SocialAccount: anything but Meta's two. */
export function isSocialChannel(channel) {
    return channel !== PublishChannel.FACEBOOK && channel !== PublishChannel.INSTAGRAM;
}
/** A social destination's dedup identity, prefixed like Instagram's. */
export function buildSocialDestinationKey(socialAccountId) {
    return `soc:${socialAccountId}`;
}
/**
 * The Instagram account linked to a Page is a **separate destination**.
 *
 * Prefixed rather than given a column of its own, so the unique index that
 * enforces duplicate prevention needs no change: one piece of content may go to
 * the Page and to its Instagram account, and neither may go twice.
 */
export function buildInstagramDestinationKey(pageId) {
    return `ig:${pageId}`;
}
/* -------------------------------------------------------------------------- */
/*  Destinations: every place something can be sent to or read from           */
/* -------------------------------------------------------------------------- */
/** What a screen wants a destination for. Each network supports some of them. */
export const DestinationFeature = {
    PUBLISH: 'publish',
    COMMENTS: 'comments',
    INBOX: 'inbox',
    MENTIONS: 'mentions',
    INSIGHTS: 'insights',
    HISTORY: 'history',
    JOBS: 'jobs',
    /** Editing a Page's own profile (about, contact, hours): a Facebook Page thing. */
    PROFILE: 'profile',
};
export function pageDestinationId(pageId) {
    return `page:${pageId}`;
}
export function socialDestinationId(socialAccountId) {
    return `soc:${socialAccountId}`;
}
/** The Instagram account linked to a Page. It is reached through that Page's id. */
export function instagramDestinationId(pageId) {
    return `ig:${pageId}`;
}
export function parseDestinationId(id) {
    if (id.startsWith('page:'))
        return { kind: 'page', id: id.slice(5) };
    if (id.startsWith('ig:'))
        return { kind: 'ig', id: id.slice(3) };
    if (id.startsWith('soc:'))
        return { kind: 'soc', id: id.slice(4) };
    return null;
}
export * from './generation.js';
export * from './socialNetwork.js';
export * from './generationProject.js';
export * from './generationGoals.js';
export * from './generationPricing.js';
export * from './generationStats.js';
//# sourceMappingURL=index.js.map